import { useState, useEffect } from 'react'
import { 
  Server, 
  Database, 
  Activity, 
  RefreshCw,
  CheckCircle,
  XCircle,
  Settings
} from 'lucide-react'
import { apiService } from '../config/api'
import toast from 'react-hot-toast'
import { useAction } from '../contexts/ActionContext'

export default function Admin() {
  const { runAction } = useAction();
  const [systemInfo, setSystemInfo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    loadAdminData()
  }, [])

  const loadAdminData = async (showLoadingSpinner = true) => {
    try {
      if (showLoadingSpinner) setIsLoading(true);
      const systemResponse = await apiService.getSystemInfo()
      setSystemInfo(systemResponse.data.data)
    } catch (error) {
      // no-op
    } finally {
      if (showLoadingSpinner) setIsLoading(false);
    }
  }

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await runAction(async () => {
      await loadAdminData(false);
    }, { message: 'Refreshing system information...' });
    setIsRefreshing(false);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400)
    const hours = Math.floor((seconds % 86400) / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-200 dark:bg-gray-800 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Server Status</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
            Monitor system health and server performance
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => {
              // Clear all onboarding flags
              localStorage.removeItem('hasSeenWelcomeGuide');
              localStorage.removeItem('hasSeenOnboarding');
              localStorage.removeItem('firstTimeUser');
              localStorage.removeItem('installationDate');
              
              toast.success('Welcome guide reset! Refresh the page to see it again.');
              
            }}
            className="btn btn-outline"
            title="Reset welcome guide for testing"
          >
            <Settings className="h-4 w-4 mr-2" />
            Reset Welcome Guide
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn btn-secondary"
          >
            {isRefreshing ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" /> Refreshing...</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Refresh</>
            )}
          </button>
        </div>
      </div>

      {/* System Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Node.js Version */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Server className="h-8 w-8 text-primary-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Node.js Version</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{systemInfo?.node_version}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Platform */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-success-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Platform</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{systemInfo?.platform} ({systemInfo?.arch})</p>
              </div>
            </div>
          </div>
        </div>

        {/* Environment */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Settings className="h-8 w-8 text-warning-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Environment</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{systemInfo?.environment}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Uptime */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Uptime</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatUptime(systemInfo?.uptime || 0)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Memory Usage */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Database className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Memory Usage</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatBytes(systemInfo?.memory_usage?.heapUsed || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  Total: {formatBytes(systemInfo?.memory_usage?.heapTotal || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Database Status */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {systemInfo?.database?.status === 'connected' ? (
                  <CheckCircle className="h-8 w-8 text-success-500" />
                ) : (
                  <XCircle className="h-8 w-8 text-danger-500" />
                )}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Database</p>
                <p className={`text-lg font-semibold capitalize ${
                  systemInfo?.database?.status === 'connected'
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {systemInfo?.database?.status || 'unknown'}
                </p>
                {systemInfo?.database?.error && (
                  <p className="text-xs text-red-500">{systemInfo.database.error}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Database Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Database Information</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Host</p>
              <p className="text-sm text-gray-900 dark:text-gray-100">{systemInfo?.database?.host}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Port</p>
              <p className="text-sm text-gray-900 dark:text-gray-100">{systemInfo?.database?.port}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-300">Database</p>
              <p className="text-sm text-gray-900 dark:text-gray-100">{systemInfo?.database?.name}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 